import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, tap } from 'rxjs';
import { Router } from '@angular/router';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { TaskResolutionEnum } from '@olv/ui-shared/enums/task.enums';
import {
  AdjustTransactionRequestDto,
  ApproveTransactionRequestDto,
  CancelTransactionDto,
  LinkedExchangeRateTransaction,
  RejectTransactionRequestDto,
  RetryTransactionDto,
  ReverseTransactionDto,
  SetTransactionRiskRequestDto,
  Transaction,
  TransactionAdjustmentReasonEnum,
  TradeTypeEnum,
  TransactionTypeEnum,
} from '@olv/ui-shared/models/merchant-transaction.model';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { SystemTransactionService } from '@olv/ui-shared/services/system-transaction.service';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { countDecimalPlaces } from '@olv/ui-shared/utils/decimal-places.util';
import { FormErrorService } from '../../services/form-error.service';
import { getTransactionExplorerUrl } from '@olv/ui-shared/utils/explorer-url.util';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { StateBadgeComponent } from '../state-badge/state-badge.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { OtpVerificationDialogComponent } from '../otp-verification-dialog/otp-verification-dialog.component';
import { AdjustTransactionDialogComponent } from '../adjust-transaction-dialog/adjust-transaction-dialog.component';
import { FormatService } from '../../services/format.service';
import { CreateReversalDialogComponent } from '../create-reversal-dialog/create-reversal-dialog.component';
import {
  RecordExchangeComponent,
  RecordExchangeDialogResult,
} from '../record-exchange-dialog/record-exchange.component';
import { SelectRiskDialogComponent } from '../select-risk-dialog/select-risk-dialog.component';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { ButtonComponent, ButtonState, toButtonState } from '../button/button.component';

@Component({
  selector: 'app-transaction-details',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    MatTooltipModule,
    HasPermissionDirective,
    ReactiveFormsModule,
    StateBadgeComponent,
    MatDividerModule,
    ButtonComponent,
  ],
  templateUrl: './transaction-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./transaction-details.component.scss'],
})
export class TransactionDetailsComponent implements OnInit {
  private merchantTransactionService = inject(MerchantTransactionService);
  private systemTransactionService = inject(SystemTransactionService);
  private taskService = inject(TaskService);
  private router = inject(Router);

  @Input() transaction!: Transaction;
  @Output() closed = new EventEmitter<void>();
  @Output() transactionUpdated = new EventEmitter<string>();

  @Input() hideHeader = false;
  @Input() taskId?: string;
  @Input() isAcknowledgeOnly = false;
  @Input() hideExchangeRateInput = false;

  private readonly appCtx = inject(AppContextService);
  private formatService = inject(FormatService);
  private auth = inject(AuthService);

  isSettlementApp = this.appCtx.getAppName() === 'settlement';
  isMerchantApp = this.appCtx.getAppName() === 'merchant';

  protected readonly manualRateTooltip = 'Exchange rate used from override value.';
  protected readonly pendingRateTooltip = 'Awaiting an exchange rate.';
  protected readonly riskUnscorableTooltip = 'Risk assessment could not be completed.';
  protected readonly riskNoRatingTooltip = 'Risk assessment returned no rating.';
  protected readonly setRiskAdvice = ' Use Set Risk to grade it.';

  warningLabelExpanded = false;
  commentText = '';
  displayCommentText = '';
  ReasonText = '';
  externalCommentText = '';
  isProcessing = false;
  errorMessage: string | null = null;
  rejectErrorMessage: string | null = null;
  sendingTooltipText = 'Copy';
  receivingTooltipText = 'Copy';
  hashTooltipText = 'Copy';
  idTooltipText = 'Copy';
  adjustmentForm: FormGroup;
  adjustmentTypeOptions = [
    { value: TransactionTypeEnum.CreditAdjustment, label: 'Credit Adjustment' },
    { value: TransactionTypeEnum.DebitAdjustment, label: 'Debit Adjustment' },
  ];
  adjustmentReasonOptions = [
    { value: TransactionAdjustmentReasonEnum.AccountOverspent, label: 'Account Overspent' },
    { value: TransactionAdjustmentReasonEnum.MerchantGoodwill, label: 'Merchant Goodwill' },
  ];

  pendingAdjustment: {
    adjustmentType: TransactionTypeEnum;
    amount: number;
    reason: TransactionAdjustmentReasonEnum;
  } | null = null;

  pendingReversal: {
    destinationWalletAddress: string;
  } | null = null;

  pendingRecordExchange: RecordExchangeDialogResult | null = null;
  pendingExchangeAction: 'create' | 'edit' | null = null;
  exchangeRate: number | null = null;
  private initializedTransactionId?: string;

  private fb = inject(FormBuilder);
  private formErrorService = inject(FormErrorService);
  private dialog = inject(MatDialog);
  private toastr = inject(ToastService);
  private readonly metadata = inject(NetworkMetadataService);

  protected readonly Scopes = Scopes;
  protected readonly Resources = Resources;
  protected readonly formatDateStandard = formatDateStandard;
  protected readonly shortenAddress = shortenAddress;

  get transactionResource() {
    return this.transaction?.merchantId ? Resources.MerchantTransactions : Resources.SystemTransactions;
  }

  get setExchangeRateResource() {
    return Resources.SystemTransactions;
  }

  get isMerchantTransaction(): boolean {
    return !!this.transaction?.merchantId;
  }

  get accountFieldLabel(): string {
    return this.isMerchantTransaction ? 'Merchant' : 'Account';
  }

  get merchantDisplayName(): string {
    if (!this.isMerchantTransaction) {
      return 'System';
    }
    return this.transaction.merchantName?.trim() || '-';
  }

  get canApprove(): boolean {
    return this.auth.hasPermission(this.transactionResource, Scopes.Approve);
  }

  get canReject(): boolean {
    return this.auth.hasPermission(this.transactionResource, Scopes.Reject);
  }

  get canApproveOrReject(): boolean {
    return this.canApprove || this.canReject;
  }

  get canAcknowledge(): boolean {
    return this.auth.hasPermission(Resources.Tasks, Scopes.Acknowledge);
  }

  get canReverse(): boolean {
    return this.transaction?.canReverse || false;
  }

  get hasSidebarActions(): boolean {
    return (
      this.showApproveReject ||
      this.showAcknowledge ||
      this.showAllowsAdjustment ||
      this.showSpeedupButton ||
      this.showCancelButton ||
      this.canCancelPrepared ||
      this.showRetry ||
      this.canReverse ||
      this.showRecordExchange ||
      this.showExchangeRateInput ||
      this.isExchange
    );
  }
  get canCancelPrepared(): boolean {
    const hasPermission =
      this.auth.hasPermission(Resources.SystemTransactions, Scopes.CancelPrepared) ||
      this.auth.hasPermission(Resources.MerchantTransactions, Scopes.CancelPrepared);

    const isDeposit = this.transaction?.direction === 'Deposit Reversal' || this.transaction?.direction === 'Deposit';

    return this.transaction?.hasAwaitingApproval && !isDeposit && hasPermission && this.transaction?.canCancelPrepared;
  }
  constructor() {
    this.adjustmentForm = this.fb.group({
      adjustmentType: [null, Validators.required],
      amount: ['', [Validators.required, this.maxTransactionAmountValidator.bind(this)]],
      adjustmentReason: [null, [Validators.required]],
    });
  }

  get showApproveReject(): boolean {
    if (this.isAcknowledgeOnly) return false;

    const isNotReports = this.router.url !== '/reports';
    const isTasksSection = this.router.url.includes('/tasks');
    const hasAwaitingApproval = this.transaction?.hasAwaitingApproval || false;
    return hasAwaitingApproval && (isNotReports || isTasksSection);
  }

  get isUnknownRiskTransaction(): boolean {
    return this.riskLevelToEnum(this.transaction?.riskLevel) === RiskLevelEnum.Unknown;
  }

  get needsRiskBeforeApproval(): boolean {
    if (this.transaction?.direction !== 'Withdrawal' && !this.isDeposit) return false;

    // An assessment that has not closed cannot be graded — the API refuses it — so this is part of the rule, not
    // a separate condition each consumer has to remember. The dialog-close guard picks it up from here too.
    if (!this.transaction?.riskAssessmentCompleted) return false;

    const level = this.riskLevelToEnum(this.transaction?.riskLevel);
    return level === RiskLevelEnum.Unknown || level === RiskLevelEnum.NotAssessed;
  }

  get approveButtonState(): 'loading' | 'disabled' | 'default' {
    if (this.isProcessing) {
      return 'loading';
    }

    if (!this.transaction.riskAssessmentCompleted && !this.isUnknownRiskTransaction) {
      return 'disabled';
    }

    return 'default';
  }

  get processingButtonState(): ButtonState {
    return toButtonState(this.isProcessing, false);
  }

  get isPendingApproval(): boolean {
    return this.transaction?.hasAwaitingApproval;
  }

  protected get riskAssessmentIndicatorTooltip(): string {
    const level = this.riskLevelToEnum(this.transaction?.riskLevel);
    const reason = level === RiskLevelEnum.Unknown ? this.riskUnscorableTooltip : this.riskNoRatingTooltip;

    // Matches the button's own condition: an open assessment hides Set Risk, so advising it would name an
    // action the operator cannot take.
    return this.isPendingApproval && this.transaction?.riskAssessmentCompleted
      ? `${reason}${this.setRiskAdvice}`
      : reason;
  }

  get showAcknowledge(): boolean {
    return this.isAcknowledgeOnly && !!this.taskId;
  }

  get canEditComments(): boolean {
    return (
      (this.showApproveReject && this.canApproveOrReject) ||
      (this.showAcknowledge && this.canAcknowledge) ||
      this.canCancelPrepared
    );
  }

  get showSpeedupButton(): boolean {
    return this.transaction?.canSpeedup || false;
  }

  get showCancelButton(): boolean {
    return this.transaction?.canCancel || false;
  }

  get showAllowsAdjustment(): boolean {
    return (this.transaction?.allowsAdjustment && this.isSettlementApp) || false;
  }

  get showRetry(): boolean {
    return this.transaction?.status.toLocaleLowerCase() === 'failed' && !this.transaction?.isRetried;
  }

  get showRecordExchange(): boolean {
    if (this.transaction?.canRecordExchange !== true) return false;

    const currency = this.transaction?.cryptocurrency?.toLocaleLowerCase();
    const isNative = this.metadata.currencies().some((c) => c.isNative && c.symbol.toLowerCase() === currency);
    if (!isNative) return false;

    const direction = this.transaction?.direction?.toLocaleLowerCase();
    const status = this.transaction?.status?.toLocaleLowerCase();
    const isTasksSection = this.router.url.includes('/tasks');

    return (
      this.isSettlementApp &&
      !isTasksSection &&
      (direction === 'withdrawal' || direction === 'deposit') &&
      (status === 'confirmed' || status === 'completed')
    );
  }

  get isExchange(): boolean {
    const direction = this.transaction?.direction?.toLocaleLowerCase();
    return direction === 'buy' || direction === 'sell';
  }

  get linkedExchangeRateTransactions(): LinkedExchangeRateTransaction[] {
    return this.transaction?.linkedExchangeRateTransactions ?? [];
  }

  // tasks and transaction history only; reports and wallet history opt out
  get showExchangeRateInput(): boolean {
    return (
      this.isSettlementApp &&
      !this.hideExchangeRateInput &&
      !!this.transaction?.requiresExchangeRateOverride &&
      this.router.url !== '/reports'
    );
  }

  private get ratePrecision(): number | undefined {
    return this.transaction?.exchangeRatePrecision ?? this.transaction?.rateDisplayPrecision;
  }

  get exchangeRateStep(): number | 'any' {
    const precision = this.ratePrecision;
    if (precision === undefined) return 'any';

    return precision > 0 ? 1 / 10 ** precision : 1;
  }

  enforceRatePrecision(event: Event): void {
    const precision = this.ratePrecision;
    const input = event.target as HTMLInputElement;
    const [whole, decimals] = input.value.split('.');
    if (precision === undefined || decimals === undefined || decimals.length <= precision) return;

    input.value = precision > 0 ? `${whole}.${decimals.slice(0, precision)}` : whole;
    this.exchangeRate = Number(input.value);
  }

  get exchangeRatePrecisionError(): string | null {
    const precision = this.ratePrecision;
    if (precision === undefined || this.exchangeRate === null) return null;

    const decimals = countDecimalPlaces(this.exchangeRate);

    return decimals > precision ? `Enter a rate with at most ${precision} decimal places` : null;
  }

  get exchangeRateBounds(): { min: number; max: number; percent: number } | null {
    const lastKnown = this.transaction?.lastKnownExchangeRate;
    const percent = this.transaction?.maxExchangeRateDeviationPercent;
    if (lastKnown == null || !(lastKnown > 0) || percent == null || !(percent > 0)) return null;

    const spread = (lastKnown * percent) / 100;
    return { min: lastKnown - spread, max: lastKnown + spread, percent };
  }

  get exchangeRateDeviationError(): string | null {
    const bounds = this.exchangeRateBounds;
    const value = Number(this.exchangeRate);
    if (!bounds || this.exchangeRate === null || Number.isNaN(value)) return null;

    if (value >= bounds.min && value <= bounds.max) return null;

    const low = this.formatWithPrecision(bounds.min, this.ratePrecision);
    const high = this.formatWithPrecision(bounds.max, this.ratePrecision);
    return `Enter a rate within ±${bounds.percent}% of the last known rate (${low} - ${high})`;
  }

  get exchangeRateError(): string | null {
    return this.exchangeRateDeviationError ?? this.exchangeRatePrecisionError;
  }

  get exchangeRateHint(): string | null {
    const bounds = this.exchangeRateBounds;
    const lastKnown = this.transaction?.lastKnownExchangeRate;
    if (!bounds || lastKnown == null) return null;

    return `Last known rate: ${this.formatWithPrecision(lastKnown, this.ratePrecision)} (±${bounds.percent}%)`;
  }

  get canSubmitExchangeRate(): boolean {
    const rate = Number(this.exchangeRate);
    return !this.isProcessing && Number.isFinite(rate) && rate > 0 && !this.exchangeRateError;
  }

  get exchangeRateButtonState(): ButtonState {
    return toButtonState(this.isProcessing, !this.canSubmitExchangeRate);
  }

  submitExchangeRate(): void {
    if (!this.canSubmitExchangeRate) return;

    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Rate Override',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((totp) => {
      this.setExchangeRate(totp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => sub.unsubscribe());
  }

  private setExchangeRate(totp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    let successToast: string | null = null;
    const request$ = this.systemTransactionService
      .setExchangeRateForPending(this.transaction.id, {
        exchangeRateToBase: Number(this.exchangeRate),
        totp,
      })
      .pipe(
        tap((result) => {
          this.exchangeRate = null;
          if (result.failed.length) {
            this.toastr.warning(`${result.failed.length} transaction(s) could not be released and stay parked`);
          }
          if (result.outstanding > 0) {
            this.toastr.warning(`${result.outstanding} more pending transaction(s) remain; submit the rate again`);
          }
          if (result.released > 0) {
            successToast = `Exchange rate applied to ${result.released} pending ${this.transaction.cryptocurrency} transaction(s)`;
          } else {
            this.toastr.warning('No pending transactions were released');
          }
        }),
      );

    this.handleExchangeRequest(request$, dialogRef, {
      successMessage: () => successToast,
      errorMessage: 'Failed to apply the exchange rate to pending transactions',
    });
  }

  ngOnInit(): void {
    this.metadata.load();
    this.setCommentText(this.transaction);
    this.initializedTransactionId = this.transaction?.id;

    console.log('Transaction details initialized with transaction:', this.transaction);
  }
  ngOnChanges(changes: SimpleChanges) {
    const TransactionChange = changes['transaction'];
    if (TransactionChange && TransactionChange.currentValue) {
      const newTransaction = TransactionChange.currentValue as Transaction;
      if (newTransaction.id !== this.initializedTransactionId) {
        this.setCommentText(newTransaction);
        this.warningLabelExpanded = false;
        this.exchangeRate = null;
        this.pendingRiskLevel = null;
        this.initializedTransactionId = newTransaction.id;
      }
      this.adjustmentForm.get('amount')?.updateValueAndValidity();
    }
  }

  private setCommentText(transaction: Transaction): void {
    if (this.isMerchantApp) {
      this.displayCommentText = transaction.externalComments || 'No Comments';
      this.commentText = transaction.comments || '';
      return;
    }

    this.commentText =
      transaction.hasAwaitingApproval || this.isAcknowledgeOnly ? '' : transaction.comments || 'No Comments';
    this.externalCommentText = transaction.externalComments || 'No External Comments';
  }
  private maxTransactionAmountValidator(control: AbstractControl) {
    const value = Number(control.value);
    const max = Number(this.transaction?.amountBaseCurrency);

    if (!value || !max) return null;

    return value > max ? { maxAmountExceeded: true } : null;
  }

  formatAmount(amount: number, includeCurrency = true): string {
    if (amount === null || amount === undefined) return '';
    const formatted = amount.toLocaleString('en-US');
    return includeCurrency ? `$${formatted}` : formatted;
  }

  private get rateValue(): number | null | undefined {
    return this.transaction?.exchangeRate ?? this.transaction?.rate;
  }

  get showRateRow(): boolean {
    return (
      this.rateValue != null ||
      (this.isSettlementApp &&
        (!!this.transaction?.isExchangeRateOverridden || !!this.transaction?.requiresExchangeRateOverride))
    );
  }

  get rateDisplay(): string {
    const rate = this.rateValue;
    if (rate == null) return 'N/A';

    return this.formatWithPrecision(rate, this.ratePrecision ?? this.transaction.baseCurrencyDisplayPrecision);
  }

  formatWithPrecision(value: number | undefined | null, precision?: number): string {
    return this.formatService.formatCoinCurrency(value, precision);
  }

  formatBaseWithPrecision(value: number | undefined | null, precision?: number, currencySymbol?: string): string {
    if (value === null || value === undefined) return '';
    const numberPart = this.formatWithPrecision(value, precision);
    return currencySymbol ? `${currencySymbol}${numberPart}` : numberPart;
  }

  resolutionToString(value: TaskResolutionEnum): string {
    return TaskResolutionEnum[value];
  }

  copyToClipboard(value: string, tooltip: MatTooltip, type: 'sending' | 'receiving' | 'hash' | 'id') {
    navigator.clipboard.writeText(value).then(() => {
      if (type === 'sending') this.sendingTooltipText = 'Copied';
      else if (type === 'receiving') this.receivingTooltipText = 'Copied';
      else if (type === 'hash') this.hashTooltipText = 'Copied';
      else if (type === 'id') this.idTooltipText = 'Copied';
      tooltip.show();

      setTimeout(() => {
        if (type === 'sending') this.sendingTooltipText = 'Copy';
        else if (type === 'receiving') this.receivingTooltipText = 'Copy';
        else if (type === 'hash') this.hashTooltipText = 'Copy';
        else if (type === 'id') this.idTooltipText = 'Copy';
      }, 1500);
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  openOtp(action: 'approve' | 'speedup' | 'cancel' | 'adjust' | 'retry' | 'reverse' | 'set-risk'): void {
    this.openOtpDialog(action);
  }

  private pendingRiskLevel: RiskLevelEnum | null = null;

  openRiskSelectionDialog(): void {
    const dialogRef = this.dialog.open(SelectRiskDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Set Risk Level',
        subtitle:
          'Choose the risk level for this transaction. The outcome is decided by the merchant\'s risk profile.',
        selectedRiskLevel: this.riskLevelToEnum(this.transaction?.riskLevel),
      },
    });

    const openedForTransactionId = this.transaction?.id;

    dialogRef.afterClosed().subscribe((riskLevel?: RiskLevelEnum) => {
      if (riskLevel === undefined || riskLevel === null) return;
      if (this.transaction?.id !== openedForTransactionId) return;
      if (!this.isPendingApproval || !this.needsRiskBeforeApproval) return;

      // The grading is routed through the risk profile in the same request, and a level the profile auto-accepts
      // releases the payout, so it takes the same OTP every other funds-moving action here does.
      this.pendingRiskLevel = riskLevel;
      this.openOtpDialog('set-risk');
    });
  }

  // The risk profile may approve, reject or leave the transaction awaiting an operator check, so the caller
  // refreshes the view rather than assuming it is unchanged — handleOtpAction already does that on success.
  private getSetRiskObservable(otp: string) {
    if (this.pendingRiskLevel === null) {
      throw new Error('Risk level missing');
    }

    const isSystem = !this.transaction.merchantId;
    const req: SetTransactionRiskRequestDto = {
      totp: otp,
      riskLevel: this.pendingRiskLevel,
      comments: this.commentText?.trim() || undefined,
    };

    if (isSystem) return this.systemTransactionService.setRisk(this.transaction.id, req);
    return this.merchantTransactionService.setRisk(this.transaction.id, req);
  }

  private riskLevelToEnum(riskLevel?: string): RiskLevelEnum | null {
    switch ((riskLevel ?? '').toLowerCase()) {
      case 'low':
        return RiskLevelEnum.Low;
      case 'medium':
        return RiskLevelEnum.Medium;
      case 'high':
        return RiskLevelEnum.High;
      case 'severe':
        return RiskLevelEnum.Severe;
      case 'unknown':
        return RiskLevelEnum.Unknown;
      case 'notassessed':
      case 'not assessed':
        return RiskLevelEnum.NotAssessed;
      default:
        return null;
    }
  }

  private openOtpDialog(
    action: 'approve' | 'speedup' | 'cancel' | 'adjust' | 'retry' | 'reverse' | 'set-risk',
  ): MatDialogRef<OtpVerificationDialogComponent, unknown> {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Transaction',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp) => {
      this.handleOtpAction(action, otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
      this.pendingRiskLevel = null;
    });

    return dialogRef;
  }

  private handleOtpAction(
    action: 'approve' | 'speedup' | 'cancel' | 'adjust' | 'retry' | 'reverse' | 'set-risk',
    otp: string,
    dialogRef: MatDialogRef<OtpVerificationDialogComponent>,
  ): void {
    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    this.isProcessing = true;
    this.errorMessage = null;

    let request$;
    switch (action) {
      case 'approve':
        request$ = this.getApproveObservable(otp);
        break;

      case 'speedup':
        request$ = this.getSpeedupObservable(otp);
        break;

      case 'cancel':
        request$ = this.getCancelObservable(otp);
        break;

      case 'retry':
        request$ = this.getRetryObservable(otp);
        break;

      case 'reverse':
        request$ = this.getReverseObservable(otp);
        break;

      case 'adjust':
        request$ = this.getAdjustObservable(otp);
        break;

      case 'set-risk':
        request$ = this.getSetRiskObservable(otp);
        break;

      default:
        throw new Error(`Unsupported OTP action: ${action}`);
    }

    request$
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          if (action === 'adjust') {
            this.pendingAdjustment = null;
          }

          if (action === 'reverse') {
            this.toastr.success('Transaction reversal completed successfully');
          }

          this.transactionUpdated.emit(this.transaction.id);
          dialogRef.close();
          this.onClose();
        },
        error: (error) => {
          if (error.isOtpError) {
            dialog.setErrorState(error.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();

          const fallbackByAction: Record<typeof action, string> = {
            approve: 'Approval failed. Please try again.',
            speedup: 'Speedup failed. Please try again.',
            cancel: 'Cancellation failed. Please try again.',
            adjust: 'Adjustment failed. Please try again.',
            retry: 'Retry failed. Please try again.',
            reverse: 'Reversal failed. Please try again.',
            'set-risk': 'Failed to set the risk level. Please try again.',
          };
          const message = error.error?.message || fallbackByAction[action];
          this.errorMessage = message;

          if (action === 'reverse') {
            this.toastr.error(message);
          }
        },
      });
  }

  private get isDeposit(): boolean {
    return this.transaction?.direction === 'Deposit';
  }

  get approveScope() {
    return this.isDeposit ? Scopes.ApproveDeposit : Scopes.Approve;
  }

  get rejectScope() {
    return this.isDeposit ? Scopes.RejectDeposit : Scopes.Reject;
  }

  get setRiskScope() {
    return Scopes.SetRisk;
  }

  private getApproveObservable(otp: string) {
    const isSystem = !this.transaction.merchantId;
    const isAdjust = this.isAdjustTransaction;
    const req: ApproveTransactionRequestDto = { comments: this.commentText, totp: otp };

    if (isSystem) {
      return this.isDeposit
        ? this.systemTransactionService.approveDeposit(this.transaction.id, req)
        : this.systemTransactionService.approveTransaction(this.transaction.id, req);
    }
    if (isAdjust) return this.merchantTransactionService.approveAdjustTransaction(this.transaction.id, req);
    return this.isDeposit
      ? this.merchantTransactionService.approveDeposit(this.transaction.id, req)
      : this.merchantTransactionService.approveTransaction(this.transaction.id, req);
  }

  private getCancelObservable(otp: string) {
    const isSystem = !this.transaction.merchantId;
    const req: CancelTransactionDto = { totp: otp };

    if (isSystem) return this.systemTransactionService.cancelTransaction(this.transaction.id, req);
    return this.merchantTransactionService.cancelTransaction(this.transaction.id, req);
  }

  private getSpeedupObservable(otp: string) {
    const isSystem = !this.transaction.merchantId;
    const req: CancelTransactionDto = { totp: otp };

    if (isSystem) return this.systemTransactionService.speedupTransaction(this.transaction.id, req);
    return this.merchantTransactionService.speedupTransaction(this.transaction.id, req);
  }

  private getAdjustObservable(otp: string) {
    if (!this.pendingAdjustment) {
      throw new Error('Adjustment data missing');
    }

    const req: AdjustTransactionRequestDto = {
      transactionId: this.transaction.id,
      adjustmentType: this.pendingAdjustment.adjustmentType,
      amount: this.pendingAdjustment.amount,
      reason: this.pendingAdjustment.reason,
      totp: otp,
    };

    return this.merchantTransactionService.adjustTransaction(req);
  }

  private getRetryObservable(otp: string) {
    const isSystem = !this.transaction.merchantId;
    const req: RetryTransactionDto = { isRetried: true, totp: otp };

    if (isSystem) return this.systemTransactionService.retryTransaction(this.transaction.id, req);
    return this.merchantTransactionService.retryTransaction(this.transaction.id, req);
  }

  private getReverseObservable(otp: string) {
    if (!this.pendingReversal) {
      throw new Error('Reversal data missing');
    }

    const req: ReverseTransactionDto = {
      destinationWalletAddress: this.pendingReversal.destinationWalletAddress,
      totp: otp,
    };

    const isSystem = !this.transaction.merchantId;
    if (isSystem) return this.systemTransactionService.reverseDeposit(this.transaction.id, req);
    return this.merchantTransactionService.reverseDeposit(this.transaction.id, req);
  }

  CancelPrepared(): void {
    if (!this.transaction.id) return;

    this.isProcessing = true;
    this.errorMessage = null;
    const cancelRequest: RejectTransactionRequestDto = {
      comments: this.commentText,
    };
    const cancel$ = this.transaction.merchantId
      ? this.merchantTransactionService.cancelPreparedTransaction(this.transaction.id, cancelRequest)
      : this.systemTransactionService.cancelPreparedTransaction(this.transaction.id, cancelRequest);

    cancel$.pipe(finalize(() => (this.isProcessing = false))).subscribe({
      next: () => {
        this.transactionUpdated.emit(this.transaction.id);
        this.closed.emit();
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Failed to cancel transaction. Please try again.';
        console.error('Error canceling transaction:', error);
      },
    });
  }
  rejectTransaction(): void {
    if (!this.transaction.id) return;

    this.isProcessing = true;
    this.rejectErrorMessage = null;

    const rejectRequest: RejectTransactionRequestDto = {
      comments: this.commentText,
    };

    const isSystem = !this.transaction.merchantId;
    const isAdjust = this.isAdjustTransaction;
    let reject$;

    if (isSystem) {
      reject$ = this.isDeposit
        ? this.systemTransactionService.rejectDeposit(this.transaction.id, rejectRequest)
        : this.systemTransactionService.rejectTransaction(this.transaction.id, rejectRequest);
    } else if (isAdjust) {
      reject$ = this.merchantTransactionService.rejectAdjustTransaction(this.transaction.id, rejectRequest);
    } else {
      reject$ = this.isDeposit
        ? this.merchantTransactionService.rejectDeposit(this.transaction.id, rejectRequest)
        : this.merchantTransactionService.rejectTransaction(this.transaction.id, rejectRequest);
    }
    reject$.pipe(finalize(() => (this.isProcessing = false))).subscribe({
      next: () => {
        this.transactionUpdated.emit(this.transaction.id);
        this.closed.emit();
      },
      error: (error) => {
        this.rejectErrorMessage = error.error?.message || 'Failed to reject transaction. Please try again.';
        console.error('Error reject transaction:', error);
      },
    });
  }

  acknowledgeTask(): void {
    if (!this.taskId) return;

    this.isProcessing = true;
    this.errorMessage = null;

    this.taskService
      .acknowledgeTask(this.taskId, {
        comment: this.commentText?.trim() || undefined,
      })
      .pipe(finalize(() => (this.isProcessing = false)))
      .subscribe({
        next: () => {
          this.transactionUpdated.emit(this.transaction.id);
          this.closed.emit();
        },
        error: (error) => {
          this.errorMessage = error.error?.message || 'Failed to acknowledge task. Please try again.';
          console.error('Error acknowledging task:', error);
        },
      });
  }

  openAdjustDialog(): void {
    const dialogRef = this.dialog.open(AdjustTransactionDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        transaction: this.transaction,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.pendingAdjustment = result;
      this.openOtp('adjust');
    });
  }
  openReverseDialog(): void {
    const dialogRef = this.dialog.open(CreateReversalDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        network: this.transaction?.network,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.pendingReversal = result;
      this.openOtp('reverse');
    });
  }
  openRecordExchangeDialog(mode: 'create' | 'edit' = 'create'): void {
    this.pendingExchangeAction = mode;

    const dialogRef = this.dialog.open(RecordExchangeComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        currency: this.transaction?.cryptocurrency,
        mode,
        initialValue: mode === 'edit' ? this.transaction : null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.pendingRecordExchange = result;
      this.openExchangeOtpDialog();
    });
  }

  openEditExchangeDialog(): void {
    this.openRecordExchangeDialog('edit');
  }

  openDeleteExchangeOtpDialog(): void {
    this.pendingExchangeAction = null;
    this.openExchangeOtpDialog('delete');
  }

  private openExchangeOtpDialog(action: 'submit' | 'delete' = 'submit'): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Transaction',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp) => {
      if (action === 'delete') {
        this.deleteExchange(otp, dialogRef);
      } else {
        this.submitExchange(otp, dialogRef);
      }
    });

    dialogRef.afterClosed().subscribe(() => sub.unsubscribe());
  }

  private submitExchange(totp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    if (!this.pendingRecordExchange) return;

    const request$ =
      this.pendingExchangeAction === 'edit'
        ? this.systemTransactionService.editExchange(this.transaction.id, {
            ...this.getChangedExchangeFieldsForRequest(),
            totp,
          })
        : this.systemTransactionService.recordExchange(this.transaction.id, {
            ...this.pendingRecordExchange,
            date: this.formatExchangeDateForRequest(this.pendingRecordExchange.date),
            totp,
          });

    this.handleExchangeRequest(request$, dialogRef, {
      successMessage:
        this.pendingExchangeAction === 'edit' ? 'Exchange updated successfully' : 'Exchange recorded successfully',
      errorMessage: 'Failed to submit exchange. Please try again.',
      clearPending: true,
    });
  }

  private deleteExchange(totp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    const request$ = this.systemTransactionService.deleteExchange(this.transaction.id, { totp });

    this.handleExchangeRequest(request$, dialogRef, {
      successMessage: 'Exchange deleted successfully',
      errorMessage: 'Failed to delete exchange. Please try again.',
    });
  }

  private handleExchangeRequest(
    request$: any,
    dialogRef: MatDialogRef<OtpVerificationDialogComponent>,
    messages: {
      successMessage: string | (() => string | null);
      errorMessage: string;
      clearPending?: boolean;
    },
  ): void {
    const dialog = dialogRef.componentInstance;

    dialog.setLoadingState(true);
    dialogRef.disableClose = true;
    this.isProcessing = true;
    this.errorMessage = null;

    request$
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          const successMessage =
            typeof messages.successMessage === 'function' ? messages.successMessage() : messages.successMessage;
          if (successMessage) this.toastr.success(successMessage);

          if (messages.clearPending) {
            this.pendingRecordExchange = null;
            this.pendingExchangeAction = null;
          }

          this.transactionUpdated.emit(this.transaction.id);
          dialogRef.close();
          this.onClose();
        },
        error: (error: any) => {
          if (error.isOtpError) {
            dialog.setErrorState(error.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          this.errorMessage = error.error?.message || messages.errorMessage;
          this.toastr.error(this.errorMessage!);
        },
      });
  }

  private getChangedExchangeFieldsForRequest(): Partial<Omit<RecordExchangeDialogResult, 'date'>> & { date?: string } {
    if (!this.pendingRecordExchange) return {};

    const changedFields: Partial<Omit<RecordExchangeDialogResult, 'date'>> & { date?: string } = {};
    const currentDate = this.transaction?.date ? this.formatExchangeDateForRequest(this.transaction.date) : '';
    const newDate = this.formatExchangeDateForRequest(this.pendingRecordExchange.date);

    if (this.pendingRecordExchange.tradeType !== this.normalizeExchangeTradeType(this.transaction?.tradeType)) {
      changedFields.tradeType = this.pendingRecordExchange.tradeType;
    }

    if (newDate !== currentDate) {
      changedFields.date = newDate;
    }

    if (Number(this.pendingRecordExchange.amount) !== Number(this.transaction?.amount ?? 0)) {
      changedFields.amount = this.pendingRecordExchange.amount;
    }

    if (Number(this.pendingRecordExchange.rate) !== Number(this.transaction?.rate ?? 0)) {
      changedFields.rate = this.pendingRecordExchange.rate;
    }

    if (Number(this.pendingRecordExchange.fees) !== Number(this.transaction?.totalFees ?? 0)) {
      changedFields.fees = this.pendingRecordExchange.fees;
    }

    return changedFields;
  }

  private formatExchangeDateForRequest(date: Date | string): string {
    return date instanceof Date ? date.toISOString() : date;
  }

  private normalizeExchangeTradeType(tradeType: unknown): TradeTypeEnum | undefined {
    if (tradeType === undefined || tradeType === null) return undefined;

    if (
      tradeType === TradeTypeEnum.Sell ||
      Number(tradeType) === TradeTypeEnum.Sell ||
      String(tradeType).toUpperCase() === 'SELL'
    ) {
      return TradeTypeEnum.Sell;
    }

    return TradeTypeEnum.Buy;
  }
  getFieldError(controlName: string, fieldName: string): string {
    return this.formErrorService.getErrorMessage(this.adjustmentForm.get(controlName), fieldName);
  }
  get isAdjustTransaction(): boolean {
    return this.transaction?.direction === 'Debit Adjustment' || this.transaction?.direction === 'Credit Adjustment';
  }
  getTransactionExplorerUrl(): string | null {
    return getTransactionExplorerUrl(this.transaction.network, this.transaction.transactionHash);
  }
  get gasFeeLabel(): string {
    const symbol = this.metadata.networks().find((n) => n.name === this.transaction?.network)?.symbol;
    return symbol ? `Gas Fee (${symbol})` : 'Gas Fee';
  }
}
